import clsx from 'clsx';
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/common/States';

export interface Column<T> {
  key: string;
  header: string;
  /** Property name the server accepts in `sort`. Omit to make the column unsortable. */
  sortKey?: string;
  render: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  sort?: string | undefined;
  onSort?: (property: string) => void;
  onRowClick?: (row: T) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  emptyIcon?: ReactNode;
}

/**
 * One table for every list screen.
 *
 * It owns the loading, empty and error states as well as the data case, so no
 * screen can accidentally render a bare table with nothing in it and leave the
 * user guessing whether the request failed or there is genuinely no data.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  onRetry,
  sort,
  onSort,
  onRowClick,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  emptyAction,
  emptyIcon,
}: DataTableProps<T>) {
  if (error !== undefined && error !== null) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  // Only a first load shows skeletons; a background refetch keeps the previous
  // rows on screen so the page does not flash empty while filters change.
  if (loading && rows === undefined) {
    return <TableSkeleton columns={Math.min(columns.length, 6)} />;
  }
  if (rows !== undefined && rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
        icon={emptyIcon}
      />
    );
  }

  const [sortProperty, sortDirection] = (sort ?? '').split(',');

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-surface-2">
            {columns.map((column) => {
              const sortable = column.sortKey !== undefined && onSort !== undefined;
              const active = sortable && sortProperty === column.sortKey;
              const SortIcon = !active ? ChevronsUpDown : sortDirection === 'desc' ? ArrowDown : ArrowUp;

              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={active ? (sortDirection === 'desc' ? 'descending' : 'ascending') : undefined}
                  className={clsx(
                    'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider',
                    active ? 'text-ink' : 'text-subtle',
                    column.className,
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.sortKey as string)}
                      className="group inline-flex items-center gap-1.5 transition-colors hover:text-ink"
                    >
                      {column.header}
                      <SortIcon
                        className={clsx(
                          'h-3.5 w-3.5 transition-opacity',
                          active ? 'opacity-100' : 'opacity-30 group-hover:opacity-60',
                        )}
                        aria-hidden="true"
                      />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody
          className={clsx(
            'stagger-children divide-y divide-line',
            // Dim during a background refetch so it is visible that the rows on
            // screen are about to be replaced.
            loading && 'pointer-events-none opacity-60 transition-opacity',
          )}
        >
          {(rows ?? []).map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
              className={clsx(
                'transition-colors duration-100',
                onRowClick !== undefined && 'cursor-pointer hover:bg-surface-2',
              )}
            >
              {columns.map((column) => (
                <td key={column.key} className={clsx('px-4 py-3 text-muted', column.className)}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
